// frontend/src/pages/Documentos.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/Documentos.css';

const Documentos = () => {
  const { user } = useAuth();
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [filtros, setFiltros] = useState({
    categoria: '',
    institucionId: '',
    estatus: '',
    fechaInicio: '',
    fechaFin: ''
  });
  const [instituciones, setInstituciones] = useState([]);
  const [showSubirModal, setShowSubirModal] = useState(false);
  const [nuevoDocumento, setNuevoDocumento] = useState({
    categoria: '',
    descripcion: '',
    archivo: null
  });
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState(null);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revision, setRevision] = useState({
    estatus: '',
    comentarios: ''
  });

  const categorias = [
    { value: 'informes_trimestrales', label: 'Informes Trimestrales' },
    { value: 'estados_financieros', label: 'Estados Financieros' },
    { value: 'actas_constitutivas', label: 'Actas Constitutivas' },
    { value: 'convenios', label: 'Convenios' },
    { value: 'informes_anuales', label: 'Informes Anuales' },
    { value: 'otros', label: 'Otros' }
  ];

  const estatusOptions = [
    { value: 'pendiente', label: 'Pendiente', color: 'warning' },
    { value: 'en_revision', label: 'En Revisión', color: 'info' },
    { value: 'aprobado', label: 'Aprobado', color: 'success' },
    { value: 'rechazado', label: 'Rechazado', color: 'danger' }
  ];

  useEffect(() => {
    cargarDocumentos();
    if (user?.role !== 'institucion') {
      cargarInstituciones();
    }
  }, [filtros]);

  const cargarDocumentos = async () => {
    try {
      setLoading(true);
      // Construir query string con filtros
      const params = new URLSearchParams();
      if (filtros.categoria) params.append('categoria', filtros.categoria);
      if (filtros.institucionId) params.append('institucionId', filtros.institucionId);
      if (filtros.estatus) params.append('estatus', filtros.estatus);
      if (filtros.fechaInicio) params.append('fechaInicio', filtros.fechaInicio);
      if (filtros.fechaFin) params.append('fechaFin', filtros.fechaFin);

      const url = `/documentos${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      setDocumentos(response.data.data);
    } catch (error) {
      console.error('Error al cargar documentos:', error);
      alert('Error al cargar los documentos');
    } finally {
      setLoading(false);
    }
  };

  const cargarInstituciones = async () => {
    try {
      const response = await api.get('/instituciones');
      setInstituciones(response.data.data || []);
    } catch (error) {
      console.error('Error al cargar instituciones:', error);
    }
  };

  const handleFileChange = (e) => {
    setNuevoDocumento({
      ...nuevoDocumento,
      archivo: e.target.files[0]
    });
  };

  const handleSubirDocumento = async (e) => {
    e.preventDefault();
    
    if (!nuevoDocumento.archivo) {
      alert('Por favor, selecciona un archivo');
      return;
    }

    if (!nuevoDocumento.categoria) {
      alert('Por favor, selecciona una categoría');
      return;
    }

    try {
      setSubiendo(true);
      const formData = new FormData();
      formData.append('archivo', nuevoDocumento.archivo);
      formData.append('categoria', nuevoDocumento.categoria);
      formData.append('descripcion', nuevoDocumento.descripcion);

      await api.post('/documentos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('Documento subido exitosamente');
      setShowSubirModal(false);
      setNuevoDocumento({ categoria: '', descripcion: '', archivo: null });
      cargarDocumentos();
    } catch (error) {
      console.error('Error al subir documento:', error);
      alert('Error al subir el documento');
    } finally {
      setSubiendo(false);
    }
  };

  const handleRevisarDocumento = (documento) => {
    setDocumentoSeleccionado(documento);
    setRevision({
      estatus: documento.estatus,
      comentarios: documento.comentarios || ''
    });
    setShowRevisionModal(true);
  };

  const handleGuardarRevision = async () => {
    try {
      await api.put(`/documentos/${documentoSeleccionado._id}/estatus`, revision);
      
      alert('Revisión guardada exitosamente');
      setShowRevisionModal(false);
      setDocumentoSeleccionado(null);
      cargarDocumentos();
    } catch (error) {
      console.error('Error al guardar revisión:', error);
      alert('Error al guardar la revisión');
    }
  };

  const handleDescargar = async (documento) => {
    try {
      const response = await api.get(`/documentos/${documento._id}/descargar`, {
        responseType: 'blob'
      });

      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', documento.nombre);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar:', error);
      alert('Error al descargar el documento');
    }
  };

  const getEstatusBadge = (estatus) => {
    const option = estatusOptions.find(o => o.value === estatus);
    return <span className={`badge badge-${option?.color || 'secondary'}`}>
      {option?.label || estatus}
    </span>;
  };

  return (
    <div className="documentos">
      <div className="documentos-header">
        <h1>Documentos</h1>
        {user?.role === 'institucion' && (
          <button 
            className="btn-primary"
            onClick={() => setShowSubirModal(true)}
          >
            + Subir Documento
          </button>
        )}
      </div>

      {/* Filtros - solo para JASP */}
      {user?.role !== 'institucion' && (
        <div className="filtros-card">
          <h3>Filtros</h3>
          <div className="filtros-grid">
            <div className="filtro-group">
              <label>Categoría</label>
              <select
                value={filtros.categoria}
                onChange={(e) => setFiltros({...filtros, categoria: e.target.value})}
                className="form-control"
              >
                <option value="">Todas</option>
                {categorias.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div className="filtro-group">
              <label>Institución</label>
              <select
                value={filtros.institucionId}
                onChange={(e) => setFiltros({...filtros, institucionId: e.target.value})}
                className="form-control"
              >
                <option value="">Todas</option>
                {instituciones.map(inst => (
                  <option key={inst._id} value={inst._id}>{inst.nombre}</option>
                ))}
              </select>
            </div>

            <div className="filtro-group">
              <label>Estatus</label>
              <select
                value={filtros.estatus}
                onChange={(e) => setFiltros({...filtros, estatus: e.target.value})}
                className="form-control"
              >
                <option value="">Todos</option>
                {estatusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="filtro-group">
              <label>Fecha inicial</label>
              <input
                type="date"
                value={filtros.fechaInicio}
                onChange={(e) => setFiltros({...filtros, fechaInicio: e.target.value})}
                className="form-control"
              />
            </div>

            <div className="filtro-group">
              <label>Fecha final</label>
              <input
                type="date"
                value={filtros.fechaFin}
                onChange={(e) => setFiltros({...filtros, fechaFin: e.target.value})}
                className="form-control"
              />
            </div>

            <div className="filtro-group filtro-actions">
              <button 
                className="btn-secondary"
                onClick={() => setFiltros({
                  categoria: '',
                  institucionId: '',
                  estatus: '',
                  fechaInicio: '',
                  fechaFin: ''
                })}
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de documentos */}
      <div className="documentos-list">
        {loading ? (
          <div className="text-center">Cargando documentos...</div>
        ) : documentos.length === 0 ? (
          <div className="no-documentos">
            <p>No hay documentos para mostrar</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Institución</th>
                  <th>Categoría</th>
                  <th>Fecha</th>
                  <th>Estatus</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {documentos.map(doc => (
                  <tr key={doc._id}>
                    <td>{doc.nombre}</td>
                    <td>{doc.institucionId?.nombre || 'N/A'}</td>
                    <td>{categorias.find(c => c.value === doc.categoria)?.label || doc.categoria}</td>
                    <td>{new Date(doc.fechaSubida).toLocaleDateString()}</td>
                    <td>{getEstatusBadge(doc.estatus)}</td>
                    <td className="acciones">
                      <button
                        className="btn-icon"
                        onClick={() => handleDescargar(doc)}
                        title="Descargar"
                      >
                        ⬇️
                      </button>
                      
                      {user?.role !== 'institucion' && doc.estatus !== 'aprobado' && (
                        <button
                          className="btn-icon"
                          onClick={() => handleRevisarDocumento(doc)}
                          title="Revisar"
                        >
                          ✏️
                        </button>
                      )}

                      {user?.role === 'super_admin_jasp' && (
                        <button
                          className="btn-icon"
                          onClick={() => {/* Implementar eliminación */}}
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal para subir documento */}
      {showSubirModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Subir nuevo documento</h2>
            <form onSubmit={handleSubirDocumento}>
              <div className="form-group">
                <label>Categoría *</label>
                <select
                  value={nuevoDocumento.categoria}
                  onChange={(e) => setNuevoDocumento({...nuevoDocumento, categoria: e.target.value})}
                  className="form-control"
                  required
                >
                  <option value="">Selecciona una categoría</option>
                  {categorias.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={nuevoDocumento.descripcion}
                  onChange={(e) => setNuevoDocumento({...nuevoDocumento, descripcion: e.target.value})}
                  className="form-control"
                  rows="3"
                  placeholder="Breve descripción del documento (opcional)"
                />
              </div>

              <div className="form-group">
                <label>Archivo *</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="form-control"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  required
                />
                <small className="text-muted">
                  Formatos permitidos: PDF, Word, Excel, imágenes (máx. 10MB)
                </small>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowSubirModal(false)}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={subiendo}
                >
                  {subiendo ? 'Subiendo...' : 'Subir documento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para revisión */}
      {showRevisionModal && documentoSeleccionado && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Revisar documento</h2>
            <p><strong>Documento:</strong> {documentoSeleccionado.nombre}</p>
            <p><strong>Institución:</strong> {documentoSeleccionado.institucionId?.nombre}</p>

            <div className="form-group">
              <label>Estatus *</label>
              <select
                value={revision.estatus}
                onChange={(e) => setRevision({...revision, estatus: e.target.value})}
                className="form-control"
                required
              >
                <option value="">Selecciona un estatus</option>
                {estatusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Comentarios</label>
              <textarea
                value={revision.comentarios}
                onChange={(e) => setRevision({...revision, comentarios: e.target.value})}
                className="form-control"
                rows="4"
                placeholder="Agrega comentarios sobre la revisión (obligatorio si es rechazado)"
              />
            </div>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowRevisionModal(false)}
              >
                Cancelar
              </button>
              <button 
                className="btn-primary"
                onClick={handleGuardarRevision}
              >
                Guardar revisión
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documentos;